import 'dayjs/locale/cs';
import 'dayjs/locale/da';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fi';
import 'dayjs/locale/fr';
import 'dayjs/locale/hi';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/nb';
import 'dayjs/locale/nl';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/ru';
import 'dayjs/locale/sv';
import 'dayjs/locale/th';
import 'dayjs/locale/zh';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import durationPlugin from 'dayjs/plugin/duration';
import isToday from 'dayjs/plugin/isToday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import i18n from 'i18n';
import { useCallback } from 'react';

import { useTranslation } from 'hooks/useTypedTranslation';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(durationPlugin);
dayjs.extend(advancedFormat);
dayjs.extend(isToday);
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few secs',
    m: 'a min',
    mm: '%d mins',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years'
  }
});

export function durationToHumanReadable(duration: number): string {
  if (duration < 60) return `0:${String(Math.floor(duration)).padStart(2, '0')}`;

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor((duration % 3600) % 60);

  return [hours, minutes, seconds]
    .filter((x, i) => x + i)
    .map((val, i) => (i === 0 ? String(val) : String(val).padStart(2, '0')))
    .join(':');
}
export function durationToHumanReadableWithDays(duration: number): string {
  const days = Math.floor(duration / (3600 * 24));
  const hours = Math.floor((duration - days * 3600 * 24) / 3600);

  return [days, hours]
    .filter((x, i) => x + i)
    .map((val, i) => (i === 0 ? String(val) + 'd' : String(val).padStart(2, '0') + 'h'))
    .join(':');
}

export const formatDate = (date: dayjs.ConfigType, format: string) => {
  const locale = i18n.language;
  const adjustedDateFormat = (() => {
    // Czech doesn't use short month format
    if (locale === 'cs') {
      return format.replace(/MMM(?!M)/g, 'MMMM');
    }
    return format;
  })();

  return dayjs(date).format(adjustedDateFormat);
};

export function format(date: string, format: string): string {
  return dayjs(date).format(format);
}

export function toDate(date: string): Date {
  return dayjs(date).toDate();
}

export function useFormatDuration() {
  const { t } = useTranslation('common');

  return useCallback(
    (durationInMinutes: number, showFullUnits = false) => {
      const durationInMinutesCeil = Math.ceil(durationInMinutes);
      const hours = Math.floor(durationInMinutesCeil / 60);
      const minutes = durationInMinutesCeil - hours * 60;

      const hoursUnit = showFullUnits ? t('hours_time_unit', { count: hours }) : t('hr_time_unit');
      const minutesUnit = showFullUnits ? t('minutes_time_unit', { count: minutes }) : t('min_time_unit');

      return ((hours > 0 ? `${hours} ${hoursUnit} ` : '') + (hours === 0 || minutes > 0 ? `${minutes} ${minutesUnit}` : '')).trim();
    },
    [t]
  );
}

export function formatDateInYYYYMMDD(date: Date): string {
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  const year = date.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
}

export function useFromNow() {
  const { t } = useTranslation('common');

  return (dateString: Date): string => {
    const currentDate = dayjs();
    const targetDate = dayjs(dateString);

    // Calculate the difference in hours
    const hoursDifference = currentDate.diff(targetDate, 'hour');

    if (hoursDifference < 48) {
      // Within 48 hours, use relative time
      if (hoursDifference < 1) {
        return targetDate.fromNow();
      } else if (hoursDifference < 24) {
        return targetDate.from(currentDate);
      } else if (hoursDifference < 48) {
        return t('yesterday');
      }
    } else {
      // Above 48 hours, use exact date format
      if (currentDate.year() !== targetDate.year()) {
        return formatDate(targetDate, 'MMM D, YYYY');
      } else {
        return formatDate(targetDate, 'MMM D');
      }
    }

    return '';
  };
}

export function getPreviousSunday(date = new Date()): Date {
  const previousMonday = new Date();
  previousMonday.setDate(date.getDate() - date.getDay());
  return previousMonday;
}

export function lessThanXDaysApart(date1: Date, date2: Date, x: number): boolean {
  const millisecondsInADay = 86400000;
  const differenceInMilliseconds = Math.abs(date1.getTime() - date2.getTime());
  const differenceInDays = differenceInMilliseconds / millisecondsInADay;
  return differenceInDays < x;
}

export function transformDayNumberToEnglish(date: Date | number): string {
  date = date instanceof Date ? date : new Date(`2021-01-${date}`);
  const dayJsObj = dayjs(date);
  return dayJsObj.format('Do');
}
